import { useMemo } from "react";
import { toast } from "sonner";
import type { CreateTaskInput, Destination, Priority, TaskStatus, TaskWithFlags, UpdateTaskInput } from "@desk/contracts";
import { describeError } from "./api";
import { outbox, type EnqueueResult } from "./outbox";
import type { OutboxEntry, OutboxOp } from "./outbox-core";
import { useHierarchy } from "./hooks";
import { formatWake, wakeToRestore } from "@/lib/dates";

function report(r: EnqueueResult, what: string) {
  if (r.status === "failed") toast.error(`Couldn't ${what}`, { description: describeError(r.error) });
}

async function run(op: OutboxOp, what: string) {
  const r = await outbox.enqueue(op, { awaitResult: true });
  report(r, what);
  return r;
}

/** Text fields carry baseRevision so edits made elsewhere are never silently overwritten. */
const REVISIONED: (keyof UpdateTaskInput)[] = ["title", "description"];

/** Create input for a duplicate of `task` (see `duplicate`). */
export function duplicateInput(task: TaskWithFlags, entries: readonly OutboxEntry[] = outbox.getSnapshot().entries): Omit<CreateTaskInput, "clientId"> {
  const pending = task.readableId === "DSK-0" ? entries.find((e) => e.op.type === "task.create" && e.op.input.clientId === task.id)?.op : undefined;
  return {
    title: task.title,
    description: task.description || undefined,
    priority: task.priority,
    labels: task.labels,
    checklist: pending?.type === "task.create" ? [...(pending.input.checklist ?? [])] : [...task.checklist].sort((x, y) => x.position - y.position).map((c) => c.title),
    areaId: task.areaId,
    spaceId: task.spaceId,
    projectId: task.projectId,
  };
}

export function useTaskActions() {
  const h = useHierarchy();
  return useMemo(() => {
    const today = h.today;
    const timezone = h.timezone;
    const actions = {
      create: (input: Omit<CreateTaskInput, "clientId"> & { clientId: string }) =>
        outbox.enqueue({ type: "task.create", input }, { awaitResult: true, dropOnFailure: true }),

      update: (task: TaskWithFlags, patch: UpdateTaskInput) => {
        const needsRevision = REVISIONED.some((k) => k in patch);
        return run(
          { type: "task.update", taskId: task.id, patch: needsRevision ? { ...patch, baseRevision: task.revision } : patch },
          "save the change",
        );
      },

      setStatus: (task: TaskWithFlags, status: TaskStatus) => {
        if (status === "done") return actions.complete(task);
        if (status === "cancelled") return actions.cancel(task);
        if ((task.status === "done" || task.status === "cancelled") && status === "todo") return actions.reopen(task);
        return run({ type: "task.update", taskId: task.id, patch: { status } }, "change the status");
      },

      setPriority: (task: TaskWithFlags, priority: Priority) =>
        run({ type: "task.update", taskId: task.id, patch: { priority } }, "change the priority"),

      complete: async (task: TaskWithFlags, opts: { undo?: boolean } = { undo: true }) => {
        const r = await run({ type: "task.action", taskId: task.id, action: "complete" }, "complete the task");
        if (opts.undo !== false && r.status !== "failed") {
          toast(`Completed ${task.readableId === "DSK-0" ? "task" : task.readableId}`, {
            action: { label: "Undo", onClick: () => void actions.reopen(task) },
          });
        }
        return r;
      },
      reopen: (task: TaskWithFlags) => run({ type: "task.action", taskId: task.id, action: "reopen" }, "reopen the task"),
      cancel: (task: TaskWithFlags) => run({ type: "task.action", taskId: task.id, action: "cancel" }, "cancel the task"),
      toggleComplete: (task: TaskWithFlags) =>
        task.status === "done" || task.status === "cancelled" ? actions.reopen(task) : actions.complete(task),

      remove: async (task: TaskWithFlags, opts: { undo?: boolean } = { undo: true }) => {
        const r = await run({ type: "task.action", taskId: task.id, action: "delete" }, "delete the task");
        if (opts.undo !== false && r.status !== "failed") {
          toast(`Deleted ${task.readableId}`, { action: { label: "Undo", onClick: () => void actions.restore(task) } });
        }
        return r;
      },
      restore: (task: TaskWithFlags) => run({ type: "task.action", taskId: task.id, action: "restore" }, "restore the task"),

      /** Snoozes and shows a quiet "Snoozed until Tue 9 AM · Undo" toast (every snooze path uses this). */
      snooze: async (task: TaskWithFlags, until: string, opts: { undo?: boolean } = { undo: true }) => {
        const previous = task.snoozedUntil;
        const r = await run({ type: "task.snooze", taskId: task.id, until }, "snooze the task");
        if (opts.undo !== false && r.status !== "failed") {
          toast(`Snoozed until ${formatWake(until, timezone)}`, {
            id: `snooze-${task.id}`,
            action: {
              label: "Undo",
              // Rescheduling an already snoozed task: Undo goes back to the earlier wake time.
              onClick: () => {
                const at = wakeToRestore(previous);
                void (at ? actions.snooze(task, at, { undo: false }) : actions.unsnooze(task));
              },
            },
          });
        }
        return r;
      },
      unsnooze: (task: TaskWithFlags) => run({ type: "task.action", taskId: task.id, action: "unsnooze" }, "unsnooze the task"),
      stillRelevant: (task: TaskWithFlags) => run({ type: "task.action", taskId: task.id, action: "review" }, "mark it reviewed"),

      move: (task: TaskWithFlags, dest: Destination) =>
        run({ type: "task.move", taskId: task.id, dest, baseRevision: undefined }, "move the task"),

      planAdd: (taskIds: string[], date = today) => run({ type: "plan.add", date, taskIds }, "add to Today"),
      planRemove: (task: TaskWithFlags, date = today) => run({ type: "plan.remove", date, taskId: task.id }, "remove from Today"),
      isPlannedToday: (task: TaskWithFlags) => task.plannedDate === today,
      toggleToday: async (task: TaskWithFlags) => {
        const name = task.readableId === "DSK-0" ? "task" : task.readableId;
        if (task.plannedDate === today) {
          const r = await actions.planRemove(task);
          if (r.status !== "failed")
            toast(`Removed ${name} from Today`, { action: { label: "Undo", onClick: () => void actions.planAdd([task.id]) } });
        } else {
          const r = await actions.planAdd([task.id]);
          if (r.status !== "failed") toast(`Added ${name} to Today`, { action: { label: "Undo", onClick: () => void actions.planRemove(task) } });
        }
      },
      /** Set or clear the "When" date (a plan entry, never a deadline). */
      setWhen: async (task: TaskWithFlags, date: string | null) => {
        if (task.plannedDate && task.plannedDate !== date) await actions.planRemove(task, task.plannedDate);
        if (date) await actions.planAdd([task.id], date);
      },

      /**
       * A new open task in the same place with the same title, notes, priority, labels, and an
       * unchecked checklist. Dates, snooze, reminder, and repeat are left unset, so a duplicate never
       * schedules anything on its own. Resolves to the new task's id (client id while offline).
       */
      duplicate: async (task: TaskWithFlags): Promise<string | null> => {
        const clientId = crypto.randomUUID();
        const r = await actions.create({ clientId, ...duplicateInput(task) });
        if (r.status === "failed") {
          toast.error("Couldn't duplicate the task", { description: describeError(r.error) });
          return null;
        }
        return r.status === "synced" && r.task ? r.task.id : clientId;
      },

      checklistAdd: (task: TaskWithFlags, title: string) =>
        run({ type: "checklist.add", taskId: task.id, title, clientId: crypto.randomUUID() }, "add the item"),
      checklistUpdate: (task: TaskWithFlags, itemId: string, patch: { title?: string; done?: boolean }) =>
        run({ type: "checklist.update", taskId: task.id, itemId, patch }, "update the item"),
      checklistDelete: (task: TaskWithFlags, itemId: string) =>
        run({ type: "checklist.delete", taskId: task.id, itemId }, "delete the item"),
      checklistReorder: (task: TaskWithFlags, ids: string[]) =>
        run({ type: "checklist.reorder", taskId: task.id, ids }, "reorder the checklist"),
    };
    return actions;
  }, [h.today, h.timezone]);
}

export type TaskActions = ReturnType<typeof useTaskActions>;
